import React, { useEffect, useState } from "react";
import { FaFolderOpen } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useCart } from "./context/CartContext";
import { apiUrl } from "../config/config";
import ProductDescriptionTabs from "./ProductDescriptionTabs";
import SEO from "./SEO";
import RelatedProduct from "./home/RelatedProduct";
import ShopDetailSkeleton from "./ShopDetailSkeleton";

const ShopDetail = () => {
  const navigate = useNavigate();
  const pathParts = window.location.pathname.split("/");
  const { refreshCart } = useCart();

  const id = pathParts[pathParts.length - 1];
  const categoryId = pathParts[3];

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [width, setWidth] = useState(1);
  const [height, setHeight] = useState(1);
  const [qty, setQty] = useState(25);

  const [previewImage, setPreviewImage] = useState(null);
  const [fileObject, setFileObject] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState({});

  const [inputs, setInputs] = useState({
    border_thread_color: "",
    special_notes: "",
  });

  const getSelectedFiltersPrice = (filterbars, selectedFiltersData) => {
    let total = 0;

    (filterbars || []).forEach((bar) => {
      const selectedValue = selectedFiltersData[bar.label];
      if (!selectedValue) return;

      const option = bar.options?.find((opt) => opt.name === selectedValue);

      if (option?.price) {
        total += Number(option.price);
      }
    });

    return total;
  };

  useEffect(() => {
    let isMounted = true;

    fetch(`${apiUrl}/api/products/${id}`)
      .then((res) => res.json())
      .then((res) => {
        if (!isMounted) return;

        const productData = res.data;
        setProduct(productData);

        if (productData?.image) {
          setActiveImage(`${apiUrl}/storage/${productData.image}`);
        }

        setWidth(Number(productData?.filter?.width_min || 1));
        setHeight(Number(productData?.filter?.height_min || 1));

        if (productData?.filter?.filterbars) {
          const defaults = {};

          productData.filter.filterbars.forEach((bar) => {
            if (bar.options && bar.options.length > 0) {
              defaults[bar.label] = bar.options[0].name;
            }
          });

          setSelectedFilters(defaults);
        }
      })
      .catch((err) => console.log(err));

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (!product) return <ShopDetailSkeleton />;

  const p = product;
  const filter = p?.filter || {};

  const getSizeAvg = (w, h) => (w + h) / 2;

  const getBasePrice = (rules, w, h) => {
    const size = getSizeAvg(w, h);

    for (let r of rules || []) {
      if (size >= parseFloat(r.min) && size <= parseFloat(r.max)) {
        return parseFloat(r.price);
      }
    }

    return 0;
  };

  const getDiscountPercent = (discounts, quantity) => {
    let discount = 0;

    (discounts || []).forEach((d) => {
      if (quantity >= Number(d.qty)) {
        discount = Number(d.discount);
      }
    });

    return discount;
  };

  const basePrice = getBasePrice(filter.rules, width, height);

  const selectedFiltersPrice = getSelectedFiltersPrice(
    filter.filterbars,
    selectedFilters
  );

  const priceBeforeDiscount = basePrice + selectedFiltersPrice;
  const discountPercent = getDiscountPercent(filter.discounts, qty);

  const finalUnitPrice =
    priceBeforeDiscount - (priceBeforeDiscount * discountPercent) / 100;

  const totalPrice = finalUnitPrice * qty;

  const getUserOrGuestId = () => {
    const userData = localStorage.getItem("user");

    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user?.id) return { user_id: user.id, guest_id: null };
      } catch (err) {
        console.error("Invalid user format in localStorage");
      }
    }

    let guestId = localStorage.getItem("guest_id");

    if (!guestId) {
      guestId = "guest_" + Math.random().toString(36).substring(2, 12);
      localStorage.setItem("guest_id", guestId);
    }

    return { user_id: null, guest_id: guestId };
  };

  const addToCart = async (redirectToCart = true) => {
    const formData = new FormData();

    formData.append("product_id", p.id);
    formData.append("product_name", p.name);
    formData.append("qty", qty);
    formData.append("price", totalPrice.toFixed(2));

    if (filter?.price_rules_enabled) {
      formData.append("width", width);
      formData.append("height", height);
    }

    formData.append("selected_filters", JSON.stringify(selectedFilters));
    formData.append("inputs", JSON.stringify(inputs));

    if (fileObject) {
      formData.append("upload_image", fileObject);
    }

    const { user_id, guest_id } = getUserOrGuestId();

    if (user_id) formData.append("user_id", user_id);
    if (guest_id) formData.append("guest_id", guest_id);

    try {
      const res = await fetch(`${apiUrl}/api/cart/add`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        refreshCart();

        if (redirectToCart) {
          navigate("/cart");
        }

        return true;
      }

      alert("Something went wrong.");
      return false;
    } catch (error) {
      console.error("Add to cart error:", error);
      return false;
    }
  };

  const buyNow = async () => {
    const success = await addToCart(false);

    if (success) {
      navigate("/checkout");
    }
  };

  return (
    <>
      <SEO
        title={product.meta_title || product.name}
        description={
          product.meta_description ||
          product.otherDescription?.replace(/<[^>]+>/g, "").slice(0, 160)
        }
        keywords={product.meta_keywords || product.name}
      />

      <div className="w-full bg-gradient-to-b from-[#eef6ff] via-white to-[#fff7ed]">
        <div className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-8 px-4 py-8 sm:px-6 sm:py-10 lg:grid-cols-2 lg:gap-10 lg:px-10 lg:py-12">
          {/* LEFT IMAGE SECTION */}
          <div className="self-start lg:sticky lg:top-28">
            <div className="overflow-hidden rounded-[24px] border border-[#e5eaf0] bg-white p-4 shadow-[0_18px_50px_rgba(15,35,54,0.10)] sm:p-5">
              <div className="flex min-h-[310px] items-center justify-center rounded-[20px] bg-[#f8fafc] p-4 sm:min-h-[430px] lg:min-h-[500px]">
                <img
                  src={activeImage || ""}
                  alt={p?.name || ""}
                  className="max-h-full max-w-full rounded-[16px] object-contain"
                />
              </div>

              <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7">
                {[
                  ...(p?.image ? [`${apiUrl}/storage/${p.image}`] : []),
                  ...(p?.galleryImages?.map(
                    (img) => `${apiUrl}/storage/${img}`
                  ) || []),
                ]
                  .slice(0, 7)
                  .map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImage(img)}
                      className={`aspect-square overflow-hidden rounded-[12px] border bg-white p-1 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                        activeImage === img
                          ? "border-[#f97316] ring-2 ring-[#f97316]/20"
                          : "border-[#e5eaf0]"
                      }`}
                    >
                      <img
                        src={img}
                        className="h-full w-full rounded-[10px] object-cover"
                        alt=""
                      />
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* RIGHT SECTION */}
          <div className="self-start lg:sticky lg:top-28">
            <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_18px_50px_rgba(15,35,54,0.10)] sm:p-7">
              <h1 className="text-[28px] font-extrabold leading-tight tracking-wide text-[#2b2b2b] sm:text-[36px] lg:text-[42px]">
                {p?.name}
              </h1>

              {p?.description_paragraph && (
                <p className="mt-4 text-[15px] font-medium leading-[27px] text-[#415164] sm:text-[16px]">
                  {p?.description_paragraph}
                </p>
              )}

              {/* FILTER BARS */}
              {filter?.filterbars?.map((bar) => (
                <div className="mt-7" key={bar.label}>
                  <h2 className="mb-4 text-[16px] font-extrabold capitalize text-[#0a2336]">
                    {bar.label}
                  </h2>

                  <div className="flex flex-wrap gap-3 sm:gap-4">
                    {bar.options?.map((opt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() =>
                          setSelectedFilters({
                            ...selectedFilters,
                            [bar.label]: opt.name,
                          })
                        }
                        className={`group flex w-[86px] flex-col items-center justify-center rounded-[16px] border px-2 py-3 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(15,35,54,0.12)] sm:w-[92px] ${
                          selectedFilters[bar.label] === opt.name
                            ? "border-[#f97316] bg-[#fff7ed] shadow-inner ring-2 ring-[#f97316]/15"
                            : "border-[#e5eaf0] bg-white hover:border-[#f97316]/50"
                        }`}
                      >
                        <img
                          src={`${apiUrl}/storage/${opt.image}`}
                          className="mb-2 h-12 w-12 rounded-full object-cover ring-2 ring-white"
                          alt={opt.name}
                        />

                        <p className="line-clamp-2 text-[11px] font-bold leading-[15px] text-[#415164]">
                          {opt.name}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* BORDER THREAD COLOR */}
              {filter?.border_thread_color && (
                <div className="mt-7">
                  <label className="text-[15px] font-extrabold text-[#0a2336]">
                    Border Thread Color
                  </label>

                  <input
                    type="text"
                    className="mt-2 w-full rounded-[14px] border border-[#dbe7f3] bg-white px-4 py-3 text-[15px] font-medium text-[#2b2b2b] shadow-sm outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-[#f97316]/60 focus:ring-4 focus:ring-[#f97316]/10"
                    placeholder="Enter border thread color"
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        border_thread_color: e.target.value,
                      })
                    }
                  />
                </div>
              )}

              {/* SPECIAL NOTES */}
              {filter?.special_notes && (
                <div className="mt-7">
                  <label className="text-[15px] font-extrabold text-[#0a2336]">
                    Special Notes
                  </label>

                  <textarea
                    className="mt-2 w-full resize-none rounded-[14px] border border-[#dbe7f3] bg-white px-4 py-3 text-[15px] font-medium text-[#2b2b2b] shadow-sm outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-[#f97316]/60 focus:ring-4 focus:ring-[#f97316]/10"
                    rows="4"
                    placeholder="Add your special notes..."
                    onChange={(e) =>
                      setInputs({ ...inputs, special_notes: e.target.value })
                    }
                  />
                </div>
              )}

              {/* UPLOAD IMAGE */}
              {filter?.upload_image && (
                <div className="mt-8 rounded-[22px] border border-[#e5eaf0] bg-gradient-to-br from-[#f8fafc] to-white p-6 text-center shadow-[0_14px_35px_rgba(15,35,54,0.08)]">
                  <FaFolderOpen className="mx-auto mb-4 animate-bounce text-6xl text-[#f97316]" />

                  <label
                    htmlFor="uploadImage"
                    className="mt-3 inline-block cursor-pointer rounded-[14px] bg-[#0a2336] px-8 py-3 text-[15px] font-extrabold uppercase tracking-[0.4px] text-white shadow-md transition duration-300 hover:scale-105 hover:bg-[#f97316]"
                  >
                    Upload Image
                  </label>

                  <input
                    id="uploadImage"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFileObject(file);
                      if (file) setPreviewImage(URL.createObjectURL(file));
                    }}
                  />

                  {previewImage && (
                    <div className="mt-6">
                      <img
                        src={previewImage}
                        className="mx-auto h-44 w-44 rounded-[18px] border border-[#e5eaf0] object-cover shadow-lg"
                        alt="Preview"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* WIDTH & HEIGHT */}
              {filter?.price_rules_enabled && (
                <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="rounded-[18px] border border-[#e5eaf0] bg-[#f8fafc] p-4">
                    <label className="mb-3 block text-[15px] font-extrabold text-[#0a2336]">
                      Width (Inches):
                    </label>

                    <div className="flex h-12 w-full max-w-[180px] items-center justify-between rounded-[14px] border border-[#dbe7f3] bg-white px-3">
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[22px] font-bold text-[#0a2336] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                        onClick={() =>
                          setWidth(Math.max(filter.width_min || 1, width - 0.5))
                        }
                      >
                        –
                      </button>

                      <span className="text-[17px] font-extrabold text-[#2b2b2b]">
                        {width}
                      </span>

                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[22px] font-bold text-[#0a2336] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                        onClick={() =>
                          setWidth(
                            Math.min(filter.width_max || 100, width + 0.5)
                          )
                        }
                      >
                        +
                      </button>
                    </div>

                    <span className="mt-2 block text-[13px] font-medium text-[#415164]">
                      Min - {filter.width_min}, Max {filter.width_max}
                    </span>
                  </div>

                  <div className="rounded-[18px] border border-[#e5eaf0] bg-[#f8fafc] p-4">
                    <label className="mb-3 block text-[15px] font-extrabold text-[#0a2336]">
                      Height (Inches):
                    </label>

                    <div className="flex h-12 w-full max-w-[180px] items-center justify-between rounded-[14px] border border-[#dbe7f3] bg-white px-3">
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[22px] font-bold text-[#0a2336] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                        onClick={() =>
                          setHeight(
                            Math.max(filter.height_min || 1, height - 0.5)
                          )
                        }
                      >
                        –
                      </button>

                      <span className="text-[17px] font-extrabold text-[#2b2b2b]">
                        {height}
                      </span>

                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[22px] font-bold text-[#0a2336] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                        onClick={() =>
                          setHeight(
                            Math.min(filter.height_max || 100, height + 0.5)
                          )
                        }
                      >
                        +
                      </button>
                    </div>

                    <span className="mt-2 block text-[13px] font-medium text-[#415164]">
                      Min - {filter.height_min}, Max - {filter.height_max}
                    </span>
                  </div>
                </div>
              )}

              {/* DISCOUNT TABLE */}
              {filter?.discounts && (
                <div className="mt-7 overflow-hidden rounded-[18px] border border-[#e5eaf0]">
                  <table className="w-full text-left">
                    <thead className="bg-[#0a2336] text-white">
                      <tr>
                        <th className="px-3 py-3 text-center text-[13px] font-bold sm:text-[14px]">
                          Qty
                        </th>
                        <th className="px-3 py-3 text-center text-[13px] font-bold sm:text-[14px]">
                          Discount (%)
                        </th>
                        <th className="px-3 py-3 text-center text-[13px] font-bold sm:text-[14px]">
                          Price After Discount
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {filter.discounts.map((d, i) => {
                        const discountPriceBefore =
                          basePrice + selectedFiltersPrice;

                        const discountedPrice =
                          discountPriceBefore -
                          (discountPriceBefore * Number(d.discount)) / 100;

                        return (
                          <tr
                            key={i}
                            className={`text-center ${
                              i % 2 === 0 ? "bg-white" : "bg-[#f8fafc]"
                            }`}
                          >
                            <td className="border-t border-[#e5eaf0] px-3 py-3 text-[13px] font-bold text-[#2b2b2b] sm:text-[14px]">
                              {d.qty}
                            </td>
                            <td className="border-t border-[#e5eaf0] px-3 py-3 text-[13px] font-bold text-[#415164] sm:text-[14px]">
                              {d.discount}%
                            </td>
                            <td className="border-t border-[#e5eaf0] px-3 py-3 text-[13px] font-extrabold text-[#f97316] sm:text-[14px]">
                              ${discountedPrice.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* QUANTITY + PRICE */}
              <div className="mt-8 grid grid-cols-1 gap-5 rounded-[22px] border border-[#e5eaf0] bg-[#f8fafc] p-5 sm:grid-cols-[220px_1fr] sm:items-center">
                <div>
                  <label className="mb-2 block text-[15px] font-extrabold text-[#0a2336]">
                    Quantity
                  </label>

                  <div className="flex h-12 w-full max-w-[190px] items-center justify-between rounded-[14px] border border-[#dbe7f3] bg-white px-3">
                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-full text-[22px] font-bold text-[#0a2336] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                      onClick={() => setQty(Math.max(25, Number(qty) - 1))}
                    >
                      –
                    </button>

                    <input
                      type="number"
                      min="25"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 25 || isNaN(val)) {
                          setQty(25);
                        } else {
                          setQty(val);
                        }
                      }}
                      className="w-16 bg-transparent text-center text-[17px] font-extrabold text-[#2b2b2b] outline-none"
                    />

                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-full text-[22px] font-bold text-[#0a2336] transition hover:bg-[#fff7ed] hover:text-[#f97316]"
                      onClick={() => setQty(Number(qty) + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="rounded-[18px] bg-white p-4 text-left sm:text-right">
                  <p className="text-[14px] font-medium text-[#415164]">
                    Base Price:{" "}
                    <span className="font-extrabold text-[#0a2336]">
                      ${(basePrice + selectedFiltersPrice).toFixed(2)}
                    </span>
                  </p>

                  <p className="mt-2 text-[16px] font-bold text-[#2b2b2b]">
                    {`$${finalUnitPrice.toFixed(2)} × ${qty}`}
                    <span className="font-extrabold text-[#f97316]">
                      {" = $" + totalPrice.toFixed(2)}
                    </span>
                  </p>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <button
                  type="button"
                  className="rounded-[14px] bg-[#0a2336] px-6 py-3.5 text-[15px] font-extrabold uppercase tracking-[0.4px] text-white shadow-[0_12px_28px_rgba(10,35,54,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f97316]"
                  onClick={addToCart}
                >
                  Add to Cart
                </button>

                <button
                  type="button"
                  className="rounded-[14px] border border-[#0a2336] bg-white px-6 py-3.5 text-[15px] font-extrabold uppercase tracking-[0.4px] text-[#0a2336] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#f97316] hover:bg-[#fff7ed] hover:text-[#f97316]"
                  onClick={buyNow}
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProductDescriptionTabs
        otherDescription={p?.otherDescription}
        additionalInformation={p?.additionalInformation}
      />

      <RelatedProduct categoryId={categoryId} />
    </>
  );
};

export default ShopDetail;